import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { getSharedCard, sharedAssetURL } from "./api";
import { isShareToken, parseSharedCard, supportsInteractiveRenderer, type CardAsset, type SharedCard } from "./types";
import { loadVerifiedSharedAsset } from "./assets";

const CardScene = lazy(async () => {
  const module = await import("./CardScene");
  return { default: module.CardScene };
});

type LoadState = "loading" | "ready" | "notFound" | "unavailable";
export interface SceneAssetURLs {
  front: string;
  back: string;
  background?: string;
  subject?: string;
  lineart?: string;
  typography?: string;
  foilMask?: string;
  exclusionMask?: string;
  particleAtlas?: string;
}

function tokenFromPath(): string {
  const match = window.location.pathname.match(/^\/c\/([^/]+)\/?$/u);
  if (!match?.[1]) return "";
  try {
    const token = decodeURIComponent(match[1]);
    return isShareToken(token) ? token : "";
  } catch {
    return "";
  }
}

function initialSnapshot(expectedToken: string): SharedCard | undefined {
  const node = document.getElementById("lorefoil-snapshot");
  if (!node?.textContent) return undefined;
  try {
    return parseSharedCard(JSON.parse(node.textContent), expectedToken);
  } catch { return undefined; }
}

export default function App() {
  const token = useMemo(tokenFromPath, []);
  const snapshot = useMemo(() => initialSnapshot(token), [token]);
  const [card, setCard] = useState<SharedCard | undefined>(snapshot);
  const [state, setState] = useState<LoadState>(snapshot ? "ready" : "loading");
  const [isFlipped, setIsFlipped] = useState(false);
  const [webGLFailed, setWebGLFailed] = useState(false);
  const [assetURLs, setAssetURLs] = useState<SceneAssetURLs>();
  const onWebGLUnavailable = useCallback(() => setWebGLFailed(true), []);

  useEffect(() => {
    if (snapshot || !token) {
      if (!snapshot) setState("notFound");
      return;
    }
    getSharedCard(token).then((value) => {
      setCard(value);
      setState("ready");
      document.title = `${value.title} · Lorefoil`;
    }).catch((error: Error) => setState(error.message === "NOT_FOUND" ? "notFound" : "unavailable"));
  }, [snapshot, token]);

  useEffect(() => {
    if (!card || !token) return;
    setAssetURLs(undefined);
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 30_000);
    let disposed = false;
    let createdURLs: string[] = [];
    const previewDescriptors: Array<[keyof SceneAssetURLs, CardAsset]> = [
      ["front", card.revision.front.preview],
      ["back", card.revision.back.preview],
    ];
    const layerDescriptors: Array<[keyof SceneAssetURLs, CardAsset]> = [];
    if (card.revision.schemaVersion === 2) {
      layerDescriptors.push(
        ["background", card.revision.front.background],
        ["subject", card.revision.front.subject],
        ["lineart", card.revision.front.lineart],
        ["typography", card.revision.front.typography],
      );
      if (card.revision.front.foilMask) layerDescriptors.push(["foilMask", card.revision.front.foilMask]);
      if (card.revision.front.exclusionMask) layerDescriptors.push(["exclusionMask", card.revision.front.exclusionMask]);
      if (card.revision.front.particleAtlas) layerDescriptors.push(["particleAtlas", card.revision.front.particleAtlas]);
    }
    const loadGroup = async (descriptors: Array<[keyof SceneAssetURLs, CardAsset]>) => {
      const results = await Promise.allSettled(descriptors.map(async ([key, descriptor]) => {
        const url = await loadVerifiedSharedAsset(sharedAssetURL(token, descriptor.assetId), descriptor, controller.signal);
        createdURLs.push(url);
        return [key, url] as const;
      }));
      const loaded: Array<readonly [keyof SceneAssetURLs, string]> = [];
      for (const result of results) {
        if (result.status === "rejected") throw result.reason;
        loaded.push(result.value);
      }
      return loaded;
    };
    void (async () => {
      try {
        const previews = await loadGroup(previewDescriptors);
        if (disposed) return;
        const previewAssets = Object.fromEntries(previews) as Pick<SceneAssetURLs, "front" | "back">;
        setAssetURLs(previewAssets);
        if (layerDescriptors.length === 0) return;
        const layerURLStart = createdURLs.length;
        try {
          const layers = await loadGroup(layerDescriptors);
          if (!disposed) setAssetURLs({ ...previewAssets, ...Object.fromEntries(layers) });
        } catch {
          const unusedLayerURLs = createdURLs.splice(layerURLStart);
          unusedLayerURLs.forEach((url) => URL.revokeObjectURL(url));
          // The verified preview remains usable when optional WebGL enhancement fails.
        }
      } catch {
        if (!disposed) setState("unavailable");
      } finally {
        window.clearTimeout(timeout);
        if (disposed) {
          createdURLs.forEach((url) => URL.revokeObjectURL(url));
          createdURLs = [];
        }
      }
    })();
    return () => {
      disposed = true;
      controller.abort();
      window.clearTimeout(timeout);
      const urls = createdURLs;
      createdURLs = [];
      urls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [card, token]);

  if (state === "loading") return <StatePage title="Opening card…" detail="Catching the light." loading />;
  if (state === "notFound") return <StatePage title="This card isn’t available" detail="The link may have been turned off or the card was removed." />;
  if (state === "unavailable" || !card) return <StatePage title="The card couldn’t load" detail="Please try this link again in a moment." action />;
  if (!assetURLs) return <StatePage title="Opening card…" detail="Catching the light." loading />;

  const frontURL = assetURLs.front;
  const backURL = assetURLs.back;
  const hasLayeredAssets = card.revision.schemaVersion !== 2
    || Boolean(assetURLs.background && assetURLs.subject && assetURLs.lineart && assetURLs.typography);
  const staticOnly = webGLFailed || !supportsInteractiveRenderer(card.revision) || !hasLayeredAssets;
  return <main className="share-page">
    <header className="topbar">
      <a className="wordmark" href="https://lorefoil.kifferliu.com" aria-label="Lorefoil home"><img src="https://lorefoil.kifferliu.com/assets/brand-mark.png" alt="" width="32" height="32" />Lorefoil</a>
      <a className="open-app" href={card.openAppURL}>Open app</a>
    </header>

    <section className="card-stage">
      <div className="aura" aria-hidden="true" />
      {staticOnly ? <img className="static-card" src={isFlipped ? backURL : frontURL} alt={`${isFlipped ? "Back" : "Front"} of ${card.title}`} onError={() => setState("unavailable")} /> :
        <Suspense fallback={<img className="static-card" src={isFlipped ? backURL : frontURL} alt={`${isFlipped ? "Back" : "Front"} of ${card.title}`} />}>
          <CardScene assets={assetURLs} manifest={card.revision} isFlipped={isFlipped} onWebGLUnavailable={onWebGLUnavailable} />
        </Suspense>}
    </section>

    <section className="card-copy">
      <div>
        <p className="eyebrow">LF / {card.displayNumber}</p>
        <h1>{card.title}</h1>
        <p className="story">{card.story}</p>
      </div>
      <button className="flip" type="button" onClick={() => setIsFlipped((value) => !value)} aria-pressed={isFlipped}>
        <span aria-hidden="true">↻</span> {isFlipped ? "Show front" : "Flip card"}
      </button>
      <p className="helper">Drag the card to catch the light.</p>
    </section>
    <footer>Made with Lorefoil</footer>
  </main>;
}

function StatePage({ title, detail, loading = false, action = false }: { title: string; detail: string; loading?: boolean; action?: boolean }) {
  return <main className="state-page">
    <div className="state-mark" aria-hidden="true">L</div>
    {loading && <div className="spinner" />}
    <h1>{title}</h1>
    <p>{detail}</p>
    {action && <button type="button" onClick={() => window.location.reload()}>Try again</button>}
  </main>;
}
